import { useState } from "react";
import {
    Alert,
    AlertColor,
    Button,
    ButtonColor,
    Container,
    ContainerSize,
    Heading,
    Icon,
    Stack,
    StackDirection,
    Text,
    Wrapper,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type AlertPageProps = Record<string, never>;

const LiveAlertDemo = () => {
    const [visible, setVisible] = useState(false);
    return (
        <>
            <div className="mb-3" id="liveAlertPlaceholder">
                {visible && (
                    <Alert
                        color={AlertColor.Success}
                        className="alert-dismissible fade show"
                    >
                        Nice, you triggered this alert message!
                        <Button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => setVisible(false)}
                        >
                            &nbsp;
                        </Button>
                    </Alert>
                )}
            </div>
            <Button color={ButtonColor.Primary} onClick={() => setVisible(true)}>
                Show live alert
            </Button>
        </>
    );
};

const DismissibleAlertDemo = () => {
    const [open, setOpen] = useState(true);
    if (!open) {
        return (
            <Button color={ButtonColor.Secondary} onClick={() => setOpen(true)}>
                Show dismissible alert again
            </Button>
        );
    }
    return (
        <Alert
            color={AlertColor.Warning}
            className="alert-dismissible fade show"
        >
            <strong>Holy guacamole!</strong> You should check in on some of those fields below.
            <Button type="button" className="btn-close" aria-label="Close" onClick={() => setOpen(false)}>
                &nbsp;
            </Button>
        </Alert>
    );
};

const AlertPage = (_props: AlertPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Alert
            </Heading>
            <Text className="mb-4">
                The <Text el="strong">Alert</Text> component is used to display feedback messages, warnings, or
                informational content to the user. Alerts can be dismissible, color-coded, and styled to fit the
                application theme. Patterns below follow{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/components/alerts/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Alerts · Bootstrap 5.3
                </Wrapper>
                , using RebootUI markup and React state instead of <Text el="code">data-bs-dismiss</Text>.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-4">
                Displays contextual feedback messages to the user.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Alert color={AlertColor.Success}>
                        A simple success alert—check it out!
                    </Alert>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AlertCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Alert with different colors
            </Heading>
            <Text className="mb-4">
                Use the <Text el="code">color</Text> prop for contextual styles{" "}
                <Text el="strong">(primary, secondary, success, danger, warning, info, light, dark).</Text>
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Alert color={AlertColor.Primary}>
                        A simple primary alert—check it out!
                    </Alert>
                    <Alert color={AlertColor.Secondary}>
                        A simple secondary alert—check it out!
                    </Alert>
                    <Alert color={AlertColor.Warning}>
                        A simple warning alert—check it out!
                    </Alert>
                    <Alert color={AlertColor.Info}>
                        A simple info alert—check it out!
                    </Alert>
                    <Alert color={AlertColor.Danger}>
                        A simple danger alert—check it out!
                    </Alert>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AlertMultiCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Icons
            </Heading>
            <Text className="mb-4">
                Combine <Text el="code">d-flex</Text> and <Text el="code">align-items-center</Text> with{" "}
                <Text el="code">Icon</Text> so the graphic lines up with the message, matching{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/components/alerts/#icons"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Icons · Bootstrap 5.3
                </Wrapper>
                . Add a visually hidden label (or extend your icon component) so the meaning is not conveyed by color
                alone.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} className="gap-2">
                        <Alert color={AlertColor.Primary} className="d-flex align-items-center mb-0">
                            <span className="visually-hidden">Info</span>
                            <Icon name="info-circle-fill" className="flex-shrink-0 me-2" width={18} />
                            <div>An example alert with an icon</div>
                        </Alert>
                        <Alert color={AlertColor.Success} className="d-flex align-items-center mb-0">
                            <span className="visually-hidden">Success</span>
                            <Icon name="check-circle-fill" className="flex-shrink-0 me-2" width={18} />
                            <div>An example success alert with an icon</div>
                        </Alert>
                        <Alert color={AlertColor.Warning} className="d-flex align-items-center mb-0">
                            <span className="visually-hidden">Warning</span>
                            <Icon name="exclamation-triangle-fill" className="flex-shrink-0 me-2" width={18} />
                            <div>An example warning alert with an icon</div>
                        </Alert>
                        <Alert color={AlertColor.Danger} className="d-flex align-items-center mb-0">
                            <span className="visually-hidden">Danger</span>
                            <Icon name="exclamation-triangle-fill" className="flex-shrink-0 me-2" width={18} />
                            <div>An example danger alert with an icon</div>
                        </Alert>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AlertIconsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Live example
            </Heading>
            <Text className="mb-4">
                Click the button to show an alert, then dismiss it with the close control. This mirrors the{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/components/alerts/#live-example"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap live alert demo
                </Wrapper>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <LiveAlertDemo />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {LiveAlertCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Dismissing
            </Heading>
            <Text className="mb-4">
                Add <Text el="code">alert-dismissible</Text> for extra padding, a <Text el="code">btn-close</Text>{" "}
                button, and <Text el="code">fade show</Text> if you use Bootstrap’s fade styles. Bootstrap also
                recommends <Text el="code">role=&quot;alert&quot;</Text> on the root element for assistive
                technologies. With React, wire <Text el="code">onClick</Text> on the close button to remove the alert
                from the tree (Bootstrap’s <Text el="code">data-bs-dismiss=&quot;alert&quot;</Text> requires the
                Bootstrap alert plugin). See{" "}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/components/alerts/#dismissing"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Dismissing · Bootstrap 5.3
                </Wrapper>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DismissibleAlertDemo />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DismissibleAlertCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default AlertPage;

const AlertCode = `<Alert color={AlertColor.Success}>
  A simple success alert—check it out!
</Alert>`;

const AlertMultiCode = `<Alert color={AlertColor.Primary}>
  A simple primary alert—check it out!
</Alert>
<Alert color={AlertColor.Secondary}>
  A simple secondary alert—check it out!
</Alert>
<Alert color={AlertColor.Warning}>
  A simple warning alert—check it out!
</Alert>
<Alert color={AlertColor.Info}>
  A simple info alert—check it out!
</Alert>
<Alert color={AlertColor.Danger}>
  A simple danger alert—check it out!
</Alert>`;

const AlertIconsCode = `import { Alert, AlertColor, Icon, Stack, StackDirection } from "@reactufy/rebootui";

<Stack direction={StackDirection.Column} className="gap-2">
  <Alert color={AlertColor.Primary} className="d-flex align-items-center mb-0">
    <span className="visually-hidden">Info</span>
    <Icon name="info-circle-fill" className="flex-shrink-0 me-2" width={18} />
    <div>An example alert with an icon</div>
  </Alert>
  <Alert color={AlertColor.Success} className="d-flex align-items-center mb-0">
    <span className="visually-hidden">Success</span>
    <Icon name="check-circle-fill" className="flex-shrink-0 me-2" width={18} />
    <div>An example success alert with an icon</div>
  </Alert>
  <Alert color={AlertColor.Warning} className="d-flex align-items-center mb-0">
    <span className="visually-hidden">Warning</span>
    <Icon name="exclamation-triangle-fill" className="flex-shrink-0 me-2" width={18} />
    <div>An example warning alert with an icon</div>
  </Alert>
  <Alert color={AlertColor.Danger} className="d-flex align-items-center mb-0">
    <span className="visually-hidden">Danger</span>
    <Icon name="exclamation-triangle-fill" className="flex-shrink-0 me-2" width={18} />
    <div>An example danger alert with an icon</div>
  </Alert>
</Stack>`;

const LiveAlertCode = `const [visible, setVisible] = useState(false);

return (
  <>
    <div id="liveAlertPlaceholder">
      {visible && (
        <Alert
          color={AlertColor.Success}
          className="alert-dismissible fade show"
        >
          Nice, you triggered this alert message!
          <Button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setVisible(false)}
          >
            &nbsp;
          </Button>
        </Alert>
      )}
    </div>
    <Button color={ButtonColor.Primary} onClick={() => setVisible(true)}>
      Show live alert
    </Button>
  </>
);`;

const DismissibleAlertCode = `const [open, setOpen] = useState(true);

return open ? (
  <Alert
    color={AlertColor.Warning}
    className="alert-dismissible fade show"
  >
    <strong>Holy guacamole!</strong> You should check in on some of those fields below.
    <Button
      type="button"
      className="btn-close"
      aria-label="Close"
      onClick={() => setOpen(false)}
    >
      &nbsp;
    </Button>
  </Alert>
) : (
  <Button color={ButtonColor.Secondary} onClick={() => setOpen(true)}>
    Show dismissible alert again
  </Button>
);`;
